// useProjectCompletionNotification.js
import { useState, useEffect, useCallback } from 'react';
import { getFirestore, collection, query, where, onSnapshot } from 'firebase/firestore';
import { authManager } from '@/managers/AuthManager';

/* ========================
 * useProjectCompletionNotification - Notifies user of newly completed projects
 * Provides a boolean flag and a reset function for UI indicators.
 * ======================== */
export const useProjectCompletionNotification = () => {
    const [authState, setAuthState] = useState(authManager.getState());
    const [hasNewCompletion, setHasNewCompletion] = useState(false);
    
    // We use localStorage to persist the timestamp of the last time the user acknowledged notifications.
    const VISTED_TIMESTAMP_KEY = `shelby_coder_last_visited_${authState.user?.uid}`;

    useEffect(() => {
        const unsubscribe = authManager.subscribe(setAuthState);
        return () => unsubscribe();
    }, []);

    const resetNotification = useCallback(() => {
        setHasNewCompletion(false);
        try {
            localStorage.setItem(VISTED_TIMESTAMP_KEY, new Date().toISOString());
        } catch (error) {
            console.error("Failed to write to localStorage:", error);
        }
    }, [VISTED_TIMESTAMP_KEY]);

    useEffect(() => {
        if (!authState.user) {
            setHasNewCompletion(false);
            return;
        }

        const firestore = getFirestore();
        const lastVisitedTimestamp = localStorage.getItem(VISTED_TIMESTAMP_KEY) || new Date(0).toISOString();
        
        const projectsQuery = query(
            collection(firestore, 'users', authState.user.uid, 'shelbulacoderprojects'),
            where('metadata.status', 'in', ['completed', 'finished'])
        );

        const unsubscribe = onSnapshot(projectsQuery, (snapshot) => {
            let foundNew = false;
            snapshot.forEach((doc) => {
                const project = doc.data();
                const completedAt = project.metadata?.completedAt?.toDate();
                if (completedAt && completedAt.toISOString() > lastVisitedTimestamp) {
                    foundNew = true;
                }
            });
            setHasNewCompletion(foundNew);
        }, (error) => {
            console.error("Error listening for project completions:", error);
        });

        return () => unsubscribe();

    }, [authState.user, VISTED_TIMESTAMP_KEY]);

    return { hasNewCompletion, resetNotification };
};